import React, { useCallback, useEffect, useState } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { useCustomer } from "@/hooks/useCustomer";
import { storeAuthData } from "@/utils/auth";
import { decodeJwtPayload, persistProductName } from "@/utils/product";
import { updateProspect, updateStripeCustomerProfile } from "@/utils/stripe";

const getWorkspace = () => {
  // Use environment variable if available
  if (import.meta.env.VITE_APP_WORKSPACE) {
    // logger.log(
    //   `Using ${import.meta.env.VITE_APP_WORKSPACE} environment from env var`,
    // );
    return import.meta.env.VITE_APP_WORKSPACE;
  }

  // Fallback to hostname-based detection
  const hostname = window.location.hostname;

  if (hostname === "staging.lexi.tips") {
    logger.log("Using staging environment");
    return "staging";
  } else if (hostname === "lexi.tips") {
    logger.log("Using production environment");
    return "prod";
  }
  logger.log("Using development environment");
  return "dev";
};

export default function UpdateContactModal({
  isOpen,
  onClose,
  type,
  currentValue,
  onUpdate,
}) {
  const [value, setValue] = useState("");
  const [error, setError] = useState("");
  const [isUpdatingLocal, setIsUpdatingLocal] = useState(false);

  const { customerId: firebaseId, stripeCustomerId } = useCustomer();

  // Validation functions
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const isValidPhone = (phone) => {
    // Remove any non-digit characters and check length
    const digitsOnly = phone.replace(/\D/g, "");
    // Australian numbers should be 10 digits (excluding country code)
    return digitsOnly.length >= 10;
  };

  const isInputValid = () => {
    if (type === "email") {
      return isValidEmail(value);
    } else {
      return isValidPhone(value);
    }
  };

  // Reset all form state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setValue("");
      setError("");
      setIsUpdatingLocal(false);
    }
  }, [isOpen]);

  // Handle escape key press
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  // Handle click outside
  const handleClickOutside = useCallback(
    (e) => {
      if (e.target.classList.contains("update-contact-modal")) {
        onClose();
      }
    },
    [onClose],
  );

  const refreshJWTAfterPayment = async () => {
    try {
      const currentToken =
        sessionStorage.getItem("authToken") ||
        localStorage.getItem("authToken");
      if (!currentToken) return;

      const response = await fetch("/api/refresh-jwt-after-payment", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: currentToken }),
      });

      if (!response.ok) return;

      const data = await response.json();
      if (data.token) {
        sessionStorage.setItem("authToken", data.token);
        localStorage.setItem("authToken", data.token);
        window.dispatchEvent(new Event("authTokenUpdated"));

        const payload = decodeJwtPayload(data.token);
        const subscription = payload?.subscription;
        const oneTimePayments = payload?.oneTimePayments;

        if (
          subscription &&
          Array.isArray(subscription) &&
          subscription.length > 0
        ) {
          const activeSub =
            subscription.find(
              (sub) => sub.status === "active" || sub.status === "trialing",
            ) || subscription[0];
          const productName = activeSub?.items?.[0]?.price?.product?.name;
          persistProductName(productName || "Lexi Social");
        } else if (
          oneTimePayments &&
          Array.isArray(oneTimePayments) &&
          oneTimePayments.length > 0
        ) {
          const mostRecent = [...oneTimePayments].sort(
            (a, b) => b.created - a.created,
          )[0];
          persistProductName(mostRecent?.productName || "Lexi Social");
        } else {
          persistProductName("Lexi Social");
        }

        const verificationSid =
          sessionStorage.getItem("verificationSid") ||
          localStorage.getItem("verificationSid");
        if (verificationSid) {
          const email = sessionStorage.getItem("userEmail");
          const phoneNumber = sessionStorage.getItem("phoneNumber");
          storeAuthData(verificationSid, email, phoneNumber, data.token);
        }
      }
    } catch (error) {
      console.error("Error refreshing JWT:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isInputValid()) {
      setError(type === "email" ? "Invalid email" : "Invalid phone");
      return;
    }

    setIsUpdatingLocal(true);
    setError("");

    try {
      const payload =
        type === "email" ? { email: value.toLowerCase() } : { mobile: value };

      // 1. Update prospect data in Firestore
      await updateProspect(firebaseId, payload);

      // 2. Update Stripe customer information if customerId exists
      if (stripeCustomerId) {
        await updateStripeCustomerProfile(stripeCustomerId, payload);
      }

      // Update storage immediately
      if (type === "email") {
        const lowerEmail = value.toLowerCase();
        sessionStorage.setItem("userEmail", lowerEmail);
        localStorage.setItem("userEmail", lowerEmail);
      } else {
        sessionStorage.setItem("phoneNumber", value);
        localStorage.setItem("phoneNumber", value);
      }

      // 3. Refresh JWT to reflect changes
      await refreshJWTAfterPayment();

      // Execute parent success logic
      if (onUpdate) {
        await onUpdate(value);
      }

      onClose();
    } catch (err) {
      setError(err.message || "Failed to update");
    } finally {
      setIsUpdatingLocal(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="update-contact-modal fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={handleClickOutside}
    >
      <div
        className="modal-inner bg-white rounded-lg w-full max-w-md p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-xl font-bold mb-4">
          Update {type === "email" ? "Email" : "Phone Number"}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          {type === "email" ? (
            <input
              type="email"
              value={value}
              onChange={(e) => {
                setValue(e.target.value);
                setError(""); // Clear error when user types
              }}
              placeholder={`Enter new email`}
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#F0A7D6] focus:border-transparent"
              required
            />
          ) : (
            <PhoneInput
              country={"au"}
              value={value}
              onChange={(phone) => {
                setValue(phone);
                setError(""); // Clear error when user types
              }}
              inputClass="w-full px-4 py-3 text-md rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#F0A7D6]"
              containerClass="w-full"
              inputStyle={{
                width: "100%",
                height: "48px",
              }}
              buttonStyle={{
                borderRadius: "8px 0 0 8px",
                borderColor: "#E5E7EB",
              }}
              enableSearch={false}
              onlyCountries={["au"]}
              placeholder={`Enter new phone number`}
              required
            />
          )}

          <button
            type="submit"
            disabled={isUpdatingLocal || !isInputValid()}
            className="w-full bg-[#F30099] text-white px-4 py-3 rounded-lg hover:bg-pink-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isUpdatingLocal ? "Updating..." : "Update"}
          </button>

          {error && (
            <div className="text-red-500 text-sm text-center">{error}</div>
          )}

          <button
            type="button"
            onClick={onClose}
            className="w-full mt-2 px-4 py-3 text-gray-600 hover:text-gray-800 transition-colors"
          >
            Cancel
          </button>
        </form>
      </div>
    </div>
  );
}
